export type Ordering = -1 | 0 | 1;

/**
 *
 * @description interface for data that form a total order
 */
export interface Order<T> {
  /**
   *
   * @description compare two elements
   */
  compare(a: T, b: T): Ordering;
}

// -- order combinators

/**
 *
 * @description Derives an order instance on type T from an order instance on type U
 */
export const contramap = <T, U>(
  order: Order<U>,
  fn: (v: T) => U
): Order<T> => ({
  compare(a, b) {
    return order.compare(fn(a), fn(b));
  },
});

export const alphanumOrder: Order<string> = {
  compare: (a, b) => a.localeCompare(b, 'fr', { numeric: true }) as Ordering,
};

/**
 *
 * @description The empty order instance, always returns 0
 */
export const emptyOrder: Order<any> = {
  compare() {
    return 0;
  },
};

/**
 *
 * @description Combines two order instances into one, using the leftmost order instance as the primary order
 */
export const concat = <T>(a: Order<T>, b: Order<T>): Order<T> => ({
  compare(x, y) {
    return a.compare(x, y) || b.compare(x, y);
  },
});

/**
 *
 * @description Combines an array of order instances into one
 */
export const concatAll = <T>(orders: Order<T>[]): Order<T> =>
  orders.reduce(concat, emptyOrder);

/**
 *
 * @description Reverses the order of an order instance
 */
export const reverse = <T>(order: Order<T>): Order<T> => ({
  compare(a, b) {
    return -order.compare(a, b) as Ordering;
  },
});

// -- order instances

/**
 *
 * @description Order instance for numbers
 */
export const numberOrder: Order<number> = {
  compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
  },
};

/**
 *
 * @description Order instance for strings
 */
export const stringOrder: Order<string> = {
  compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
  },
};

/**
 *
 * @description Order instance for booleans
 */
export const booleanOrder: Order<boolean> = {
  compare(a, b) {
    return a === b ? 0 : a ? 1 : -1;
  },
};

/**
 *
 * @description Order instance for Date objects
 */
export const dateOrder: Order<Date> = contramap(numberOrder, (d) =>
  d.getTime()
);
